import React from 'react';

function StatusBadge({ value }) {
  if (!value) return null;
  const tones = {
    active: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
    inactive: 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
    draft: 'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;',
    submitted: 'background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;',
    validated: 'background:#fff7ed;color:#c2410c;border:1px solid #fed7aa;',
    approved: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
    rejected: 'background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;',
  };
  const style = tones[String(value).toLowerCase()] ?? tones.draft;
  const label = value.charAt(0).toUpperCase() + value.slice(1);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 10px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 600,
        ...Object.fromEntries(
          style.split(';').filter(Boolean).map((s) => {
            const [k, v] = s.split(':').map((x) => x.trim());
            const key = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
            return [key, v];
          })
        ),
      }}
    >
      {label}
    </span>
  );
}

function DetailRow({ label, value, mono = false, wide = false }) {
  const display = value === null || value === undefined || value === '' ? '-' : value;
  return (
    <div style={{ gridColumn: wide ? '1 / -1' : undefined }} className="grid gap-1.5">
      <span className="text-[13px] font-semibold text-gray-900">{label}</span>
      <div
        className={`rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5 text-sm text-gray-700 ${
          mono ? 'font-mono text-[11px] break-all' : ''
        }`}
      >
        {display}
      </div>
    </div>
  );
}

/**
 * Generic record details popup.
 *
 * Props:
 *   isOpen      - boolean
 *   onClose     - () => void
 *   onEdit      - () => void - opens edit modal
 *   onDelete    - async () => void - called after confirm
 *   title       - string  e.g. "Institution Details"
 *   subtitle    - string  primary record name / identifier
 *   statusValue - string  e.g. "active" | "draft" | "approved"
 *   fields      - [{ label, value, mono?, wide? }]
 *   deleting    - boolean  shows spinner on delete button
 *   editLabel   - string  default "Edit Record"
 *   deleteLabel - string  default "Delete"
 */
export default function RecordDetailsModal({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  title = 'Record Details',
  subtitle,
  statusValue,
  fields = [],
  deleting = false,
  editLabel = 'Edit Record',
  deleteLabel = 'Delete',
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-gray-900/45 px-4 py-10"
    >
      <div
        className="w-full max-w-[560px] rounded-2xl bg-white shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5">
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-full text-xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            x
          </button>
        </div>

        <div className="px-6 pb-2">
          {(subtitle || statusValue) && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="min-w-0 truncate text-sm font-semibold text-gray-900">
                {subtitle ?? '-'}
              </p>
              {statusValue && <StatusBadge value={statusValue} />}
            </div>
          )}

          <div className="grid gap-3 pb-4 sm:grid-cols-2">
            {fields.map((f) => (
              <DetailRow key={f.label} label={f.label} value={f.value} mono={f.mono} wide={f.wide} />
            ))}
          </div>
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-12 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="h-12 px-5 rounded-full border border-red-200 bg-red-50 text-sm font-semibold text-red-600 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleting ? 'Deleting...' : deleteLabel}
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="flex-1 h-12 rounded-full bg-green-500 text-sm font-semibold text-white hover:bg-green-600"
            >
              {editLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
